import React, { useState, useEffect, useRef, useMemo } from 'react'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { FunnelIcon } from '@heroicons/react/24/outline'
import MonitorCard from 'components/Dashboard/MonitorCard'
import StatCards from 'components/Dashboard/StatCards'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useTableData } from 'hooks/useTableData'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary, resolveDashboardDateRangeFilter } from 'utils/dateRangeFilters'

const EMPTY_BOX_FILTER = { containerId: '', containerName: '' }

// The box type filter only applies to packing, so it is kept apart from the
// shared dashboard date range (which is persisted and reused by every dashboard).
const buildPackingSummaryQuery = (dateRange, boxFilter) => {
  const params = new URLSearchParams(buildDashboardDateRangeQuery(dateRange))
  if (boxFilter.containerId) params.set('containerId', boxFilter.containerId)
  return params.toString()
}

const toFileNamePart = value => String(value).trim().replace(/[^\w.-]+/g, '_')

// GET request for packing summary data
const getPackingSummaryData = async (farmId, dateRange, boxFilter) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/packing-summary?${buildPackingSummaryQuery(dateRange, boxFilter)}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching packing summary data:', error)
    throw error
  }
}

// GET request for the box types (containers) used by the box type filter
const getContainers = async () => {
  const response = await makeRequest({
    method: 'GET',
    url: '/management/containers',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('secret')}`
    }
  })
  return Array.isArray(response.data) ? response.data : []
}

const themeColorMap = (shade) => {
  return {
    700: 'bg-green-700',
    600: 'bg-green-600',
    500: 'bg-green-500',
    400: 'bg-green-400'
  }[shade]
}

const DailyPackingSummary = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const filterOptions = useDashboardFilterOptions(user?.farmId)

  // Get current date in Mexico City timezone for date picker
  const currentDateForPicker = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')

  const [dateRange, setDateRange] = useDashboardDateRange(currentDateForPicker)
  const [showDateRangeModal, setShowDateRangeModal] = useState(false)
  const [boxFilter, setBoxFilter] = useState(EMPTY_BOX_FILTER)
  const [containers, setContainers] = useState([])

  // State for API data
  const [packingSummaryData, setPackingSummaryData] = useState({
    dailyPackingSummary: {
      totalPacked: 0,
      packerCount: 0,
      averagePerWorker: 0,
      averagePackedPerHour: 0
    },
    dailyPackingSummarySameHourPreviousDay: {
      totalPacked: 0,
      packerCount: 0
    },
    packerGroupsMonitor: [],
    packersMonitor: [],
    loading: true,
    error: null
  })

  const fetchPackingSummaryData = async (rangeToFetch = dateRange, boxFilterToFetch = boxFilter) => {
    if (!user?.farmId) return

    try {
      setPackingSummaryData(prev => ({ ...prev, loading: true, error: null }))

      const data = await getPackingSummaryData(user.farmId, rangeToFetch, boxFilterToFetch)

      setPackingSummaryData(prev => ({
        ...prev,
        dailyPackingSummary: data.dailyPackingSummary || prev.dailyPackingSummary,
        dailyPackingSummarySameHourPreviousDay: data.dailyPackingSummarySameHourPreviousDay || prev.dailyPackingSummarySameHourPreviousDay,
        packerGroupsMonitor: data.packerGroupsMonitor || [],
        packersMonitor: data.packersMonitor || [],
        loading: false
      }))
    } catch (error) {
      console.error('Error fetching packing summary:', error)
      setPackingSummaryData(prev => ({
        ...prev,
        loading: false,
        error: t('dashboard.packing.error')
      }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    fetchPackingSummaryData()
    getContainers()
      .then(setContainers)
      .catch(error => console.error('Failed to load containers:', error))
  }, [user?.farmId])

  useEffect(() => {
    if (!hasFetched.current) return
    fetchPackingSummaryData(dateRange, boxFilter)
  }, [dateRange, boxFilter])

  // The modal edits the date range and the box type together.
  const filterModalData = useMemo(() => ({ ...dateRange, ...boxFilter }), [dateRange, boxFilter])

  const handleApplyFilters = (nextRange) => {
    const { containerId = '', containerName = '', ...nextDateRange } = nextRange
    setDateRange(nextDateRange)
    setBoxFilter({ containerId, containerName: containerId ? containerName : '' })
    setShowDateRangeModal(false)
  }

  const filterSummary = [getDateRangeSummary(dateRange), boxFilter.containerName]
    .filter(Boolean)
    .join(' · ')

  // Export headings/file names carry the applied filters (box type + range).
  const exportProps = (title, fileName) => {
    const resolvedRange = resolveDashboardDateRangeFilter(dateRange)
    const fileNameParts = [
      fileName,
      boxFilter.containerName,
      resolvedRange.dateFrom,
      resolvedRange.dateTo !== resolvedRange.dateFrom ? resolvedRange.dateTo : ''
    ].filter(Boolean).map(toFileNamePart)

    return {
      exportTitle: [title, boxFilter.containerName].filter(Boolean).join(' - '),
      exportSubtitle: getDateRangeSummary(dateRange),
      exportFileName: fileNameParts.join('_')
    }
  }

  // Create cards data from API response
  const createCardsData = () => {
    const { dailyPackingSummary, dailyPackingSummarySameHourPreviousDay } = packingSummaryData
    const selectedRangeFormatted = filterSummary

    const packingRange = t('dashboard.packing.packingRange', { range: selectedRangeFormatted })
    const samePreviousDay = t('dashboard.packing.samePreviousDay')

    return [
      {
        title: t('dashboard.packing.totalPacked'),
        value: Number(dailyPackingSummary.totalPacked || 0).toLocaleString(),
        unit: t('columns.units'),
        sub: packingRange,
        color: 'bg-green-700'
      },
      {
        title: t('dashboard.packing.totalPacked'),
        value: Number(dailyPackingSummarySameHourPreviousDay.totalPacked || 0).toLocaleString(),
        unit: t('columns.units'),
        sub: samePreviousDay,
        color: themeColorMap(500)
      },
      {
        title: t('dashboard.packing.packersCount'),
        value: Number(dailyPackingSummary.packerCount || 0).toString(),
        unit: t('common.workers'),
        sub: packingRange,
        color: 'bg-green-700'
      },
      {
        title: t('dashboard.packing.packersCount'),
        value: Number(dailyPackingSummarySameHourPreviousDay.packerCount || 0).toString(),
        unit: t('common.workers'),
        sub: samePreviousDay,
        color: themeColorMap(500)
      },
      {
        title: t('dashboard.packing.avgPerPacker'),
        value: Number(dailyPackingSummary.averagePerWorker || 0).toFixed(2),
        unit: t('columns.units'),
        sub: packingRange,
        color: 'bg-green-600'
      },
      {
        title: t('dashboard.packing.workerAvg'),
        value: Number(dailyPackingSummary.averagePackedPerHour || 0).toFixed(2),
        unit: t('dashboard.unitsPerHour'),
        sub: packingRange,
        color: 'bg-green-700'
      }
    ]
  }

  // Use the custom hook to generate table data with totals
  const packerGroupsTableData = useTableData(packingSummaryData.packerGroupsMonitor, 'packerGroups')
  const packerMonitorTableData = useTableData(packingSummaryData.packersMonitor, 'packerMonitor')

  if (packingSummaryData.loading) {
    return <PageLoadingState message={t('dashboard.packing.loading')} rows={12} />
  }

  if (packingSummaryData.error) {
    return (
      <div className='p-6 bg-gray-50 min-h-screen'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-red-600'>{packingSummaryData.error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>

      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-green-600'>{t('dashboard.packing.title')}</h1>
          <p className='text-sm text-gray-600'>
            {t('dashboard.changeFilters')}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-700'>{filterSummary}</span>
          <ButtonComponent className='flex items-center gap-2' onClick={() => setShowDateRangeModal(true)}>
            <FunnelIcon className='w-5 h-5' />
            <span>{t('dashboard.advancedFilters')}</span>
          </ButtonComponent>
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      <StatCards cards={createCardsData()} />

      <MonitorCard title={t('dashboard.packing.packerGroupsMonitor')} date={filterSummary} hasData={packerGroupsTableData.rows.length > 0} color='green'>
        <TableComponent
          tableData={packerGroupsTableData}
          defaultSortColumn={packerGroupsTableData.columns[0]?.key}
          {...exportProps(t('dashboard.packing.packerGroupsMonitor'), 'packer-groups-monitor')}
        />
      </MonitorCard>

      <MonitorCard title={t('dashboard.packing.packerMonitor')} date={filterSummary} hasData={packerMonitorTableData.rows.length > 0} color='green'>
        <TableComponent
          tableData={packerMonitorTableData}
          defaultSortColumn={packerMonitorTableData.columns[0]?.key}
          {...exportProps(t('dashboard.packing.packerMonitor'), 'packer-monitor')}
        />
      </MonitorCard>
      <DateRangeFilterModal
        isOpen={showDateRangeModal}
        dateRange={filterModalData}
        sectors={filterOptions.sectors}
        farmingBlocks={filterOptions.farmingBlocks}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        containers={containers}
        maxDate={currentDateForPicker}
        onClose={() => setShowDateRangeModal(false)}
        onApply={handleApplyFilters}
      />
    </div>
  )
}

export default DailyPackingSummary
